/* eslint-disable @typescript-eslint/ban-ts-comment */
// Types
import type { ClientConfig, ConfigOptions, RequestBody } from '@white-label-types/parking-checkout';
import type { AxiosRequestConfig } from 'axios';

// Services
import { httpBooking } from '@white-label-configuration/http';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppPaymentMerchantId } from '@white-label-helper/get-app-payment-merchant-id';
import { getAppPaymentProvider } from '@white-label-helper/get-app-payment-provider';

import { PAYMENT_METHOD_STRATEGIES, PAYMENT_PROVIDERS, PaymentProvider } from '@white-label-configuration/constants';
import { isBookingPortal } from '@white-label-helper/is-booking-portal';
import { myApp } from '@white-label-plugin/global-app-context';
import { datadogRum } from '@datadog/browser-rum';


// @ts-ignore
httpBooking.onRequest((config) => {
  const session_id = datadogRum?.getInternalContext()?.session_id || null;
  if (isBookingPortal) {
    // @ts-ignore
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${myApp.$auth?.user['https://user_metadata/channel_token']}`;
  }
  config.headers['X-Datadog-Session-ID'] = session_id;
});

/**
 * * Constructs the request body for the /client-config endpoint based on the payment method.
 * @param windcaveConfig - Configuration options specific to the Windcave payment method.
 * @returns The request body object to be sent to the /client-config endpoint.
 */
const getRequestBody = (windcaveConfig: ConfigOptions): RequestBody => {
  const merchantId = getAppPaymentMerchantId() || '';
  const isStripe = getAppPaymentProvider() === PAYMENT_PROVIDERS.STRIPE;

  const requestBody: RequestBody = {
    MerchantId: merchantId,
  };

  if (isStripe) {
    requestBody.ClientConfigStrategyType = PAYMENT_METHOD_STRATEGIES.NEW_PAYMENT_ONLY;
  }

  if (windcaveConfig) {
    requestBody.WindcaveCreateClientConfigRequest = {
      Amount: windcaveConfig.amount,
      Currency: windcaveConfig.currency,
      LanguageCode: getAppVariable('default_language'),
      PartnerId: getAppVariable('partner_id'),
    }
  }

  return requestBody;
};

export const getClientConfig = (config: ConfigOptions): Promise<ClientConfig['result']> => {
  const token = getAppVariable('channel.token');
  return httpBooking
    .post('/payment-gateway/client-config', {
      headers: { Authorization: `Bearer ${token}` },
      body: getRequestBody(config),
    })
    .then(({ body }: { body: ClientConfig }) => body.result);
}

/**
 * Handles the booking process for a pole by repeatedly checking the transaction status
 * and resolving or rejecting based on the response.
 *
 * @param sessionId - The unique identifier for the session/transaction being monitored.
 * @param interval - The interval duration in milliseconds to check the transaction status. Must be greater than 1.
 * @return  Returns an async function that checks the current status of the transaction.
 * It resolves with a success message if authorized or rejected with an error message for a failed status.
 */
export function poolingBooking(sessionId: string, interval: number) {
  if (interval < 1) {
    console.warn('Interval needs to be greater than 1');
  }

  async function checkCondition(resolve: (arg0: string) => void, reject: (arg0: Error) => void) {
    const windcaveStatusResponse = await httpBooking.get(`/payment/windcave:${sessionId}/status`);

    const body = windcaveStatusResponse.body.result;
    switch (body.status) {
      case 'Authorized': {
        resolve('Windcave: Authorized');
        break;
      }

      case 'Unknown': {
        setTimeout(checkCondition, interval);
        break;
      }

      default: {
        reject(Error(`status: ${body.status}, reason: ${body.statusReason}`));
        break;
      }
    }
  }

  return new Promise(checkCondition);
}

export interface VoidRequestBody {
  Id: string,
  PSP: PaymentProvider,
  MerchantId: string,
}

/**
 * Sends a void request to the Payment API via the Booking API.
 *
 * @param {VoidRequestBody} body - contains session id and PSP name.
 * @returns {Promise<AxiosRequestConfig>}
 */
export const handleVoidRequest = (body: VoidRequestBody): Promise<AxiosRequestConfig> => (
  httpBooking.post('payment-gateway/psptransactions/void', { body })
);
